import { ProductLabel } from "../../ui/product-label.tsx";
import { useQuery } from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";
import type * as C from "@zooi/contracts";
import { useEffect, useRef, useState } from "react";
import { api, q } from "../../api.ts";
import { relative } from "../../format.ts";
import { INBOX_LABELS as I, WORK_ORDER_LABELS } from "../../labels.ts";
import { Button, Callout, ErrorText, Kbd, Pill } from "../../ui.tsx";
import { Excerpt, PointList } from "../../ui/review.tsx";
import { Skeleton } from "../../ui/skeleton.tsx";
import { useInbox, Review, DetailPanelShell, metricNamer } from "./shared.tsx";

// Part of the inbox (docs/design-system.md §0.4, plan M7 step 7g).

/** One approval lets one run start: once a run used it, it is spent whatever its expiry. */
function approvalText(approval: C.ExecutionOrder["approval"], run: C.ExecutionOrder["run"], lapsed: boolean) {
  if (!approval) return "–";
  if (approval.revoked_at) return I.approvalRevoked;
  if (run && Date.parse(run.started_at) >= Date.parse(approval.approved_at)) return I.approvalUsed(run.generation);
  if (approval.expires_at) return lapsed ? I.approvalLapsed(approval.expires_at) : I.approvedUntil(approval.expires_at);
  return relative(approval.approved_at);
}

type PullRequestSpec = {
  title?: string; why?: string; repository?: string; base_ref?: string | null; attribution_unit?: string; authorship?: string;
  acceptance?: string[]; measurement?: { mode?: string; primary_metric_id?: string | null; window_days?: number | null };
  playbook?: { id: string; version: number } | null;
};

export function WorkOrderReview() {
  const { orderId } = useParams({ strict: false }) as { orderId: string };
  const { items, intent, done } = useInbox();
  const order = useQuery(q.workOrder(orderId));
  const item = items.find((entry) => entry.kind === "work_order" && entry.id === orderId);
  const pid = item?.product.id ?? "";
  const metrics = useQuery({ ...q.metrics(pid), enabled: !!pid });
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState<"approve" | "cancel" | null>(null);
  const [error, setError] = useState<unknown>();
  const approveRef = useRef<HTMLButtonElement>(null);

  useEffect(() => { setConfirming(false); setError(undefined); }, [orderId]);
  useEffect(() => { if (order.data && intent === "approve") approveRef.current?.focus(); }, [intent, order.data, orderId]);

  if (order.isLoading || metrics.isLoading) return <DetailPanelShell><Skeleton shape="panel" /></DetailPanelShell>;
  if (!order.data) return <DetailPanelShell><ErrorText error={order.error} /></DetailPanelShell>;
  const data = order.data;
  const spec = data.spec as PullRequestSpec;
  const metric = metricNamer(metrics.data);
  const approval = data.approval;
  const lapsed = !approval || approval.revoked_at !== null || (approval.expires_at !== null && Date.parse(approval.expires_at) <= Date.now());
  const waiting = data.status === "failed" || data.status === "reconciling" || (data.status === "approved" && lapsed);
  const run = data.run;
  // The server accepts only pull request URLs; still, never link anything but https.
  const receiptUrl = data.receipt && typeof data.receipt.url === "string" && data.receipt.url.startsWith("https://") ? data.receipt.url : null;
  const act = async (kind: "approve" | "cancel") => {
    setBusy(kind); setError(undefined);
    try {
      if (kind === "approve") await api.post(`/api/work-orders/${encodeURIComponent(data.id)}/approve`, { revision: data.revision, digest: data.digest, context_hash: data.context_hash });
      else await api.post(`/api/work-orders/${encodeURIComponent(data.id)}/cancel`, {});
      done(kind === "approve" ? I.approvedAgain : I.cancelled);
    } catch (cause) { setError(cause); }
    finally { setBusy(null); }
  };

  return <Review label={I.detail} title={spec.title ?? item?.title ?? I.workOrder}
    meta={<><Pill tone={data.status === "failed" ? "red" : waiting ? "teal" : "gray"} status>{item ? I.reason[item.reason.code] ?? WORK_ORDER_LABELS.orderStatus[data.status] : WORK_ORDER_LABELS.orderStatus[data.status] ?? data.status}</Pill>{item && <ProductLabel product={item.product} />}</>}
    actions={waiting && (confirming
      ? <><Button tone="danger" busy={busy === "cancel"} disabled={busy !== null} onClick={() => void act("cancel")}>{I.confirmCancel}</Button><Button tone="ghost" disabled={busy !== null} onClick={() => setConfirming(false)}>{I.keep}</Button></>
      : <><Button ref={approveRef} tone="primary" busy={busy === "approve"} disabled={busy !== null} onClick={() => void act("approve")}>{I.approveAgain} <Kbd>A</Kbd></Button><Button tone="soft" disabled={busy !== null} onClick={() => setConfirming(true)}>{I.cancelOrder}</Button></>)}>
    {!waiting && <Callout>{I.closed}</Callout>}
    {data.status === "reconciling" && <Callout tone="warn">{I.reconcilingNote}</Callout>}
    {spec.why && <Excerpt className="z-agent-text" lines={2}>{spec.why}</Excerpt>}
    <dl className="z-props">
      <dt>{I.workOrder}</dt><dd>{WORK_ORDER_LABELS.orderKind[data.kind] ?? data.kind}</dd>
      {spec.repository && <><dt>{I.repository}</dt><dd className="z-mono">{spec.repository}{spec.base_ref ? ` · ${spec.base_ref}` : ""}</dd></>}
      {spec.attribution_unit && <><dt>{I.where}</dt><dd className="z-mono">{spec.attribution_unit}</dd></>}
      {spec.authorship && <><dt>{I.tier}</dt><dd>{I.authorship[spec.authorship] ?? spec.authorship}</dd></>}
      {spec.playbook && <><dt>{I.playbook}</dt><dd className="z-mono">{I.playbookVersion(spec.playbook.id, spec.playbook.version)}</dd></>}
      {spec.measurement?.mode && <><dt>{I.measurement}</dt><dd>{I.mode[spec.measurement.mode] ?? spec.measurement.mode}{spec.measurement.mode === "observational" && <> · {metric(spec.measurement.primary_metric_id ?? null)} · {spec.measurement.window_days ? I.windowDays(spec.measurement.window_days) : "–"}</>}</dd></>}
      {spec.acceptance && spec.acceptance.length > 0 && <><dt>{I.acceptance}</dt><dd><PointList items={spec.acceptance} /></dd></>}
      <dt>{I.status}</dt><dd>{WORK_ORDER_LABELS.orderStatus[data.status] ?? data.status}</dd>
      <dt>{I.approval}</dt><dd>{approvalText(approval, run, lapsed)}</dd>
      {run && <><dt>{I.lastRun}</dt><dd>{I.run(run.generation)} · {WORK_ORDER_LABELS.runStatus[run.status] ?? run.status}{run.end_reason && <> · {I.runEnd[run.end_reason] ?? run.end_reason}</>}{run.ended_at && <> · {I.ended(run.ended_at)}</>}</dd></>}
      {receiptUrl && <><dt>{I.receipt}</dt><dd><a href={receiptUrl} target="_blank" rel="noreferrer noopener">{I.openReceipt}</a></dd></>}
    </dl>
    <ErrorText error={error} />
  </Review>;
}

/**
 * A post the person publishes from their own account (plan M4 step 4b-1): the
 * agent's draft or points, the channel's rules, and the link they hand back.
 */
